import { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useWorkspace } from '../contexts/WorkspaceContext';
import { useAuth } from '../contexts/AuthContext';
import { taskAPI, workspaceAPI } from '../lib/api';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { TaskStatusIndicator } from '../components/TaskStatusIndicator';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '../components/ui/sheet';
import { Tabs, TabsList, TabsTrigger } from '../components/ui/tabs';
import { StellarSelect } from '../components/StellarFormControls';
import { roleLabel } from '../components/UserChip';
import { ChevronLeft, ChevronRight, X, Flag, Calendar as CalendarIcon, Filter } from 'lucide-react';
import { formatTaskDate, formatHeaderDate, localDateStr } from '../lib/dateFormat';
import { getTodayLocal, getLocalDateString } from '../utils/date';
import { TaskDetailSheet } from '../components/TaskDetailSheet';
import { useCanViewAllTasks } from '../hooks/useRoleAccess';

const PRIORITY_DOT = { high: 'bg-red-500/70', medium: 'bg-gold/70', low: 'bg-foreground/20' };
const DAY_NAMES = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

function getTaskColor(task, todayStr) {
  if (task.status === 'completed') return 'bg-green-500/[0.04] border-l-green-400/50';
  if (task.due_date && task.due_date < todayStr) return 'bg-red-500/[0.04] border-l-red-500/50';
  const diff = task.due_date ? Math.ceil((new Date(task.due_date + 'T00:00:00') - new Date(todayStr + 'T00:00:00')) / 86400000) : 99;
  if (diff <= 2) return 'bg-amber-500/[0.03] border-l-amber-400/50';
  return 'bg-foreground/[0.01] border-l-foreground/10';
}

export default function TaskCalendarPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { currentWorkspace } = useWorkspace();
  const { user } = useAuth();
  const canChooseView = useCanViewAllTasks();
  const [tasks, setTasks] = useState([]);
  // D52a CAL-011 — view-state URL persistence. Initial values read from
  // ?date=YYYY-MM-DD&view=monthly|weekly|daily&status=&priority= so a deep-link
  // restores the exact calendar configuration.
  const initialDate = (() => {
    const d = searchParams.get('date');
    if (d && /^\d{4}-\d{2}-\d{2}$/.test(d)) {
      const [y, m, day] = d.split('-').map(Number);
      return new Date(y, m - 1, day);
    }
    return new Date();
  })();
  const [currentDate, setCurrentDate] = useState(initialDate);
  const [viewMode, setViewMode] = useState(() => {
    const v = searchParams.get('view');
    return ['monthly', 'weekly', 'daily'].includes(v) ? v : 'monthly';
  });
  // D52a CAL-008 — Status + Priority filter chips.
  const [statusFilter, setStatusFilter] = useState(searchParams.get('status') || 'all');
  const [priorityFilter, setPriorityFilter] = useState(searchParams.get('priority') || 'all');
  const [selectedTaskId, setSelectedTaskId] = useState(null);
  const [sidePanelDate, setSidePanelDate] = useState(null);
  // RD1 (CAL-003) — Keyboard roving-tabindex for the monthly grid.
  // Arrow keys move focus across day cells, Enter opens that day's list,
  // Escape returns focus to the toolbar's Today button.
  const todayStr = getLocalDateString(getTodayLocal());
  const [focusedDate, setFocusedDate] = useState(todayStr);
  const cellRefs = useRef({});
  const gridRef = useRef(null);
  const toolbarTodayRef = useRef(null);
  const handleGridKey = useCallback((e) => {
    const key = e.key;
    if (!['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight', 'Enter', 'Escape', 'Home', 'End'].includes(key)) return;
    if (key === 'Enter') { e.preventDefault(); setSidePanelDate(focusedDate); return; }
    if (key === 'Escape') { e.preventDefault(); toolbarTodayRef.current?.focus(); return; }
    e.preventDefault();
    const [y, m, d] = focusedDate.split('-').map(Number);
    const cur = new Date(y, m - 1, d);
    const next = new Date(cur);
    if (key === 'ArrowLeft') next.setDate(cur.getDate() - 1);
    else if (key === 'ArrowRight') next.setDate(cur.getDate() + 1);
    else if (key === 'ArrowUp') next.setDate(cur.getDate() - 7);
    else if (key === 'ArrowDown') next.setDate(cur.getDate() + 7);
    else if (key === 'Home') next.setDate(cur.getDate() - cur.getDay());
    else if (key === 'End') next.setDate(cur.getDate() + (6 - cur.getDay()));
    const nextStr = localDateStr(next);
    setFocusedDate(nextStr);
    // If the focus moved off-month, advance currentDate.
    if (next.getMonth() !== cur.getMonth() || next.getFullYear() !== cur.getFullYear()) {
      setCurrentDate(next);
    }
  }, [focusedDate]);
  // After focusedDate changes, focus the corresponding cell ref.
  useEffect(() => {
    const el = cellRefs.current[focusedDate];
    if (el && gridRef.current?.contains(document.activeElement)) {
      el.focus();
    }
  }, [focusedDate]);
  const [teamMembers, setTeamMembers] = useState([]);
  // Phase 2 Group A — local wall-clock date, not UTC. Drives the "TODAY"
  // highlight in the monthly/weekly grid; must match the user's physical
  // calendar in Saskatchewan and India alike.

  // Batch 1 ITEM 9 (BUG-RW-CAL-USER-001) — browser tab title reflects the
  // renamed surface. Restore to default on unmount so other pages aren't
  // stuck with the calendar title.
  useEffect(() => {
    const previous = document.title;
    document.title = 'Deadline Calendar | Stellar CRM';
    return () => { document.title = previous; };
  }, []);

  // Viewing filter (Owner/Admin only — Manager/Team are forced to "mine" by backend
  // and cannot toggle, see useCanViewAllTasks single-source-of-truth hook).
  // Values: 'mine' | 'team' | '<userId>'. Persisted per-user in localStorage.
  const tw_key = `stellar_calendar_view_${user?.id || 'guest'}`;
  const [viewerFilter, setViewerFilter] = useState(() => {
    if (!canChooseView) return 'mine';
    const v = typeof window !== 'undefined' ? localStorage.getItem(tw_key) : null;
    return v || 'mine';
  });
  const setViewerFilterPersist = (v) => {
    setViewerFilter(v);
    if (user?.id) localStorage.setItem(tw_key, v);
  };

  // Fetch workspace members (used by Specific Person dropdown)
  useEffect(() => {
    if (!currentWorkspace?.id) return;
    workspaceAPI.listMembers(currentWorkspace.id).then(res => setTeamMembers(res.data)).catch(() => {});
  }, [currentWorkspace?.id]);

  // Edge case: if a stored specific-person ID is no longer a workspace member, fall back to 'mine'.
  useEffect(() => {
    if (!canChooseView) return;
    if (viewerFilter === 'mine' || viewerFilter === 'team') return;
    if (teamMembers.length === 0) return; // wait for load
    const memberIds = teamMembers.map(m => m.user_id || m.user?.id).filter(Boolean);
    if (!memberIds.includes(viewerFilter)) {
      setViewerFilterPersist('mine');
    }
  }, [viewerFilter, teamMembers, canChooseView]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchTasks = useCallback(() => {
    if (!currentWorkspace?.id) return;
    // Translate viewer filter → backend assigned_to param.
    // Manager/Team users have backend enforcement — assigned_to query param is ignored
    // server-side and forced to their own user id.
    const params = { parent_only: true };
    if (canChooseView) {
      if (viewerFilter === 'mine') params.assigned_to = user?.id;
      else if (viewerFilter === 'team') { /* no assigned_to → all */ }
      else params.assigned_to = viewerFilter; // specific user UUID
    } else {
      params.assigned_to = user?.id;
    }
    taskAPI.list(currentWorkspace.id, params).then(res => setTasks(res.data || [])).catch(() => {});
  }, [currentWorkspace?.id, viewerFilter, canChooseView, user?.id]);

  useEffect(() => { fetchTasks(); }, [fetchTasks]);
  // BUG-07 — global refresh signal + 15s poll fallback.
  useEffect(() => {
    const onChanged = () => fetchTasks();
    window.addEventListener('stellar-task-changed', onChanged);
    const pollId = setInterval(onChanged, 15_000);
    return () => {
      window.removeEventListener('stellar-task-changed', onChanged);
      clearInterval(pollId);
    };
  }, [fetchTasks]);

  // D52a CAL-011 — keep ?date=&view=&status=&priority= in sync with state.
  useEffect(() => {
    const next = new URLSearchParams(searchParams);
    next.set('date', localDateStr(currentDate));
    next.set('view', viewMode);
    if (statusFilter && statusFilter !== 'all') next.set('status', statusFilter); else next.delete('status');
    if (priorityFilter && priorityFilter !== 'all') next.set('priority', priorityFilter); else next.delete('priority');
    setSearchParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentDate, viewMode, statusFilter, priorityFilter]);

  // D52a CAL-008 — apply Status + Priority filters before bucketing into dates.
  // 'all' keeps the original behavior. 'open' = anything not completed/cancelled.
  const filteredTasks = useMemo(() => {
    return tasks.filter((t) => {
      if (statusFilter && statusFilter !== 'all') {
        if (statusFilter === 'open') {
          if (t.status === 'completed' || t.status === 'cancelled') return false;
        } else if (t.status !== statusFilter) {
          return false;
        }
      }
      if (priorityFilter && priorityFilter !== 'all' && t.priority !== priorityFilter) return false;
      return true;
    });
  }, [tasks, statusFilter, priorityFilter]);

  // ── Build the date → task-occurrences map ──
  // Tasks appear on their due_date as a 'due' occurrence + on each follow_ups[].date
  // as a 'followup' occurrence. If a follow-up date collides with due_date (shouldn't
  // happen — backend validates fu.date < due_date — but defensively) the due-date
  // occurrence wins (only rendered once with due styling).
  const occurrencesByDate = useMemo(() => {
    const map = {};
    filteredTasks.forEach(t => {
      const dueKey = t.due_date ? t.due_date.slice(0, 10) : null;
      const seen = new Set();
      if (dueKey) {
        (map[dueKey] = map[dueKey] || []).push({ task: t, kind: 'due', key: `${t.id}-due` });
        seen.add(dueKey);
      }
      (t.follow_ups || []).forEach((fu, i) => {
        if (!fu?.date) return;
        const fuKey = fu.date.slice(0, 10);
        if (seen.has(fuKey)) return; // collision with due_date — suppress duplicate, due-date wins
        (map[fuKey] = map[fuKey] || []).push({ task: t, kind: 'followup', followUp: fu, followUpIndex: i, key: `${t.id}-fu-${i}` });
        seen.add(fuKey);
      });
    });
    return map;
  }, [filteredTasks]);

  const getOccurrencesForDate = (ds) => occurrencesByDate[ds] || [];

  const year = currentDate.getFullYear();
  const month = currentDate.getMonth();
  const day = currentDate.getDate();

  const nav = (dir) => {
    if (viewMode === 'monthly') setCurrentDate(new Date(year, month + dir, 1));
    else if (viewMode === 'weekly') setCurrentDate(new Date(year, month, day + dir * 7));
    else setCurrentDate(new Date(year, month, day + dir));
  };

  const getTasksForDate = (ds) => getOccurrencesForDate(ds);

  const navLabel = viewMode === 'monthly' ? currentDate.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })
    : viewMode === 'weekly' ? (() => { const mon = new Date(year, month, day - currentDate.getDay()); const sun = new Date(mon); sun.setDate(mon.getDate() + 6); return `${formatTaskDate(localDateStr(mon))} – ${formatTaskDate(localDateStr(sun))}`; })()
    : formatHeaderDate(currentDate);

  // Build the Viewing dropdown options (Owner/Admin only)
  const viewingOptions = useMemo(() => {
    const opts = [
      { value: 'mine', label: 'My Tasks' },
      { value: 'team', label: 'Team (everyone)' },
    ];
    teamMembers.forEach(m => {
      const uid = m.user_id || m.user?.id;
      const name = m.user?.name || m.user?.email || '?';
      // Iter 65a — UserChip rendering site #4 (Calendar Viewing dropdown).
      // <option> can only render text, so we suffix the role label using the
      // shared roleLabel() helper for visual consistency with UserChip.
      const role = m.role || m.user?.role;
      const label = role ? `${name} (${roleLabel(role)})` : name;
      if (uid && uid !== user?.id) {
        opts.push({ value: uid, label });
      }
    });
    return opts;
  }, [teamMembers, user?.id]);

  // Task pill — click opens TaskDetailSheet. Renders Flag prefix + muted color
  // when this is a follow-up occurrence (vs the due-date occurrence).
  const TaskCard = ({ occurrence, compact = true }) => {
    const { task, kind } = occurrence;
    const isFollowUp = kind === 'followup';
    const baseColor = getTaskColor(task, todayStr);
    // Muted variant for follow-ups so users can tell at a glance this is a checkpoint, not deadline.
    const muteClasses = isFollowUp ? 'opacity-65 border-dashed' : '';
    return (
      <button
        onClick={(e) => { e.stopPropagation(); setSelectedTaskId(task.id); }}
        className={`flex items-center gap-1.5 w-full p-1 rounded border-l-2 text-left hover:bg-foreground/[0.04] hover:opacity-100 transition-all ${baseColor} ${muteClasses}`}
        data-testid={`calendar-task-${task.id}${isFollowUp ? `-fu-${occurrence.followUpIndex}` : ''}`}
        title={isFollowUp ? `Follow-up checkpoint${occurrence.followUp?.label ? ': ' + occurrence.followUp.label : ''}` : 'Due date'}
      >
        {isFollowUp ? (
          <Flag className="h-2.5 w-2.5 text-muted-foreground/60 shrink-0" data-testid="calendar-followup-icon" />
        ) : (
          task.status !== 'completed' && task.status !== 'cancelled' && (
            <span className={`h-1.5 w-1.5 rounded-full shrink-0 ${PRIORITY_DOT[task.priority]}`} />
          )
        )}
        <span className={`${compact ? 'text-[12px]' : 'text-[13px]'} font-medium truncate flex-1`}>{task.title}</span>
        {task.legacy_subtask_count > 0 && (
          <span className="text-[8px] px-1 py-0.5 rounded-full bg-gold/10 text-gold border border-gold/30 shrink-0"
                data-testid={`calendar-subtask-count-${task.id}`}>
            {task.legacy_subtask_count} sub
          </span>
        )}
      </button>
    );
  };

  const sidePanelTasks = sidePanelDate ? getTasksForDate(sidePanelDate) : [];

  return (
    <div className="max-w-6xl mx-auto p-4 md:p-8 space-y-4" data-testid="task-calendar-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <h1 className="text-xl md:text-2xl font-display font-bold tracking-tight">Deadline Calendar</h1>
        <div className="flex items-center gap-2 flex-wrap">
          {canChooseView && (
            <div className="flex items-center gap-1.5" data-testid="calendar-viewing-wrapper">
              <span className="text-[11px] text-muted-foreground/50">Viewing</span>
              <StellarSelect
                value={viewerFilter}
                onValueChange={setViewerFilterPersist}
                options={viewingOptions}
                size="sm"
                className="w-44"
                testId="calendar-viewing-select"
              />
            </div>
          )}
          <Tabs value={viewMode} onValueChange={setViewMode}>
            <TabsList className="h-8">
              <TabsTrigger value="monthly" className="text-[11px] h-6 px-2.5" data-testid="calendar-tab-monthly">Month</TabsTrigger>
              <TabsTrigger value="weekly" className="text-[11px] h-6 px-2.5" data-testid="calendar-tab-weekly">Week</TabsTrigger>
              <TabsTrigger value="daily" className="text-[11px] h-6 px-2.5" data-testid="calendar-tab-daily">Day</TabsTrigger>
            </TabsList>
          </Tabs>
          <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => nav(-1)} aria-label="Previous month" data-testid="calendar-prev-btn"><ChevronLeft className="h-4 w-4" /></Button>
          {/* Title rendered between nav buttons; aria-live so screen-readers
              announce the new month when nav arrows are pressed. */}
          <h2 className="text-[15px] font-semibold text-foreground min-w-[160px] text-center" aria-live="polite" data-testid="calendar-title">{navLabel}</h2>
          <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => nav(1)} aria-label="Next month" data-testid="calendar-next-btn"><ChevronRight className="h-4 w-4" /></Button>
          <Button ref={toolbarTodayRef} variant="ghost" size="sm" className="text-[11px] h-7" onClick={() => setCurrentDate(new Date())} aria-label="Jump to today" data-testid="calendar-today-btn">Today</Button>
          {/* D52a CAL-004 — explicit date picker in toolbar. Jumps the calendar
              to the picked date (and pivots month/week view to contain it). */}
          <label className="inline-flex items-center gap-1 text-[11px] text-muted-foreground/60 cursor-pointer">
            <CalendarIcon className="h-3.5 w-3.5 text-muted-foreground/50" />
            <input
              type="date"
              value={localDateStr(currentDate)}
              onChange={(e) => {
                if (!e.target.value) return;
                const [y, m, d] = e.target.value.split('-').map(Number);
                setCurrentDate(new Date(y, m - 1, d));
              }}
              className="text-[11px] bg-foreground/[0.02] border border-border/30 rounded-md px-2 py-1 outline-none focus:ring-1 focus:ring-gold/40 [color-scheme:dark]"
              aria-label="Pick a calendar date"
              data-testid="calendar-date-picker"
            />
          </label>
        </div>
      </div>

      {/* D52a CAL-008 — Status + Priority filter chips. Hidden by default,
          revealed via the Filter button. Persisted in URL via CAL-011. */}
      <div className="flex flex-wrap items-center gap-2" data-testid="calendar-filter-chips">
        <div className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider text-muted-foreground/50">
          <Filter className="h-3 w-3" /> Filter
        </div>
        {/* Status chips */}
        {[
          { value: 'all',         label: 'All status'  },
          { value: 'open',        label: 'Open' },
          { value: 'in_progress', label: 'In progress' },
          { value: 'review',      label: 'In review' },
          { value: 'completed',   label: 'Completed' },
        ].map((opt) => (
          <button
            key={`s-${opt.value}`}
            onClick={() => setStatusFilter(opt.value)}
            className={`text-[10px] rounded-full px-2 py-0.5 border transition-colors ${
              statusFilter === opt.value
                ? 'bg-gold/15 text-gold border-gold/40'
                : 'bg-foreground/[0.02] text-muted-foreground/70 border-border/30 hover:bg-foreground/[0.05]'
            }`}
            data-testid={`calendar-filter-status-${opt.value}`}
          >
            {opt.label}
          </button>
        ))}
        <span className="text-muted-foreground/30 text-[10px]">·</span>
        {/* Priority chips */}
        {[
          { value: 'all',    label: 'All priority' },
          { value: 'high',   label: 'High' },
          { value: 'medium', label: 'Medium' },
          { value: 'low',    label: 'Low' },
        ].map((opt) => (
          <button
            key={`p-${opt.value}`}
            onClick={() => setPriorityFilter(opt.value)}
            className={`text-[10px] rounded-full px-2 py-0.5 border transition-colors ${
              priorityFilter === opt.value
                ? 'bg-gold/15 text-gold border-gold/40'
                : 'bg-foreground/[0.02] text-muted-foreground/70 border-border/30 hover:bg-foreground/[0.05]'
            }`}
            data-testid={`calendar-filter-priority-${opt.value}`}
          >
            {opt.label}
          </button>
        ))}
        {(statusFilter !== 'all' || priorityFilter !== 'all') && (
          <button
            onClick={() => { setStatusFilter('all'); setPriorityFilter('all'); }}
            className="text-[10px] text-muted-foreground/50 hover:text-foreground underline-offset-2 hover:underline ml-1"
            data-testid="calendar-filter-clear"
          >
            Clear filters
          </button>
        )}
      </div>

      {/* Legend */}
      <div className="flex items-center gap-4 text-[10px] text-muted-foreground">
        <span className="flex items-center gap-1"><span className="h-2 w-6 rounded-sm bg-red-500/[0.15] border-l-2 border-red-500/50" /> Overdue</span>
        <span className="flex items-center gap-1"><span className="h-2 w-6 rounded-sm bg-amber-500/[0.1] border-l-2 border-amber-400/50" /> Due soon</span>
        <span className="flex items-center gap-1"><span className="h-2 w-6 rounded-sm bg-green-500/[0.1] border-l-2 border-green-400/50" /> Completed</span>
        {canChooseView && (
          <span className="ml-auto text-[11px] text-muted-foreground/60" data-testid="calendar-scope-subtitle">
            Showing tasks for:
            <span className="ml-1 text-foreground font-medium">
              {viewerFilter === 'mine'
                ? 'My Tasks'
                : viewerFilter === 'team'
                  ? 'Team (everyone)'
                  : (teamMembers.find(m => (m.user_id || m.user?.id) === viewerFilter)?.user?.name
                    || teamMembers.find(m => (m.user_id || m.user?.id) === viewerFilter)?.user?.email
                    || 'Selected member')}
            </span>
          </span>
        )}
      </div>

      {/* ─── MONTHLY VIEW ─── D53: mobile uses smaller cells + scroll-safe min width */}
      {viewMode === 'monthly' && (
        <Card className="card-hover-gold"><CardContent className="p-2 overflow-x-auto">
          <div className="min-w-[640px] sm:min-w-0">
          <div className="grid grid-cols-7 mb-1">{DAY_NAMES.map(d => <div key={d} className="text-center text-[11px] font-semibold text-muted-foreground/50 py-2">{d}</div>)}</div>
          <div ref={gridRef} role="grid" aria-label="Calendar grid" onKeyDown={handleGridKey} className="grid grid-cols-7 gap-1" data-testid="calendar-grid">
            {(() => {
              const firstDay = new Date(year, month, 1).getDay();
              const daysInMonth = new Date(year, month + 1, 0).getDate();
              const cells = [];
              for (let i = 0; i < firstDay; i++) cells.push(<div key={`e-${i}`} className="min-h-[80px]" />);
              for (let d = 1; d <= daysInMonth; d++) {
                const ds = `${year}-${String(month + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
                const dayTasks = getTasksForDate(ds);
                const isToday = ds === todayStr;
                const dayOfWeek = (firstDay + d - 1) % 7;
                const isWeekend = dayOfWeek === 0 || dayOfWeek === 6;
                const cellBg = isToday
                  ? 'border-gold/40 bg-gold/[0.06] ring-1 ring-gold/20'
                  : isWeekend
                    ? 'border-transparent bg-foreground/[0.025] hover:bg-foreground/[0.04]'
                    : 'border-transparent bg-foreground/[0.01] hover:bg-foreground/[0.03]';
                const isFocused = focusedDate === ds;
                cells.push(
                  <div
                    key={d}
                    ref={el => { if (el) cellRefs.current[ds] = el; }}
                    role="gridcell"
                    tabIndex={isFocused ? 0 : -1}
                    aria-label={`${ds}${isToday ? ', today' : ''}${dayTasks.length ? `, ${dayTasks.length} task${dayTasks.length > 1 ? 's' : ''}` : ''}`}
                    aria-selected={isFocused}
                    className={`min-h-[80px] rounded-lg p-1 border cursor-pointer transition-colors group relative ${cellBg} ${isFocused ? 'outline outline-2 outline-gold/60 outline-offset-[-2px]' : ''}`}
                    onClick={() => { setFocusedDate(ds); setSidePanelDate(ds); }}
                    onFocus={() => setFocusedDate(ds)}
                    data-testid={`calendar-cell-${ds}`}
                  >
                    <div className="flex items-center justify-between px-0.5">
                      <span className={`text-[11px] font-semibold ${isToday ? 'text-gold' : isWeekend ? 'text-muted-foreground/60' : 'text-muted-foreground/40'}`}>{d}</span>
                      {isToday && <span className="text-[8px] text-gold/70 font-semibold">TODAY</span>}
                      {/* Batch 5 R2 B-Block CAL-006/CAL-007 — empty-day click → New Task with date pre-filled. */}
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/tasks/all?new=true&due_date=${ds}`);
                        }}
                        className="opacity-0 group-hover:opacity-100 text-[10px] text-gold/70 hover:text-gold px-1.5 rounded hover:bg-gold/10 transition-opacity inline-flex items-center gap-0.5"
                        title={`Create task due on ${ds}`}
                        data-testid={`calendar-new-task-${ds}`}
                        aria-label={`Create task for ${ds}`}
                      >
                        + New
                      </button>
                    </div>
                    <div className="mt-0.5 space-y-0.5">
                      {dayTasks.slice(0, 3).map(occ => <TaskCard key={occ.key} occurrence={occ} />)}
                      {dayTasks.length > 3 && (
                        <button
                          onClick={(e) => { e.stopPropagation(); setSidePanelDate(ds); }}
                          className="text-[8px] text-gold/70 hover:text-gold px-1 underline-offset-2 hover:underline"
                          data-testid={`calendar-overflow-${ds}`}
                        >
                          +{dayTasks.length - 3} more
                        </button>
                      )}
                    </div>
                  </div>
                );
              }
              return cells;
            })()}
          </div>
          </div>
        </CardContent></Card>
      )}

      {/* ─── WEEKLY VIEW ─── D53: scroll on mobile */}
      {viewMode === 'weekly' && (
        <Card className="card-hover-gold"><CardContent className="p-2 overflow-x-auto">
          <div className="grid grid-cols-7 gap-2 min-w-[640px] sm:min-w-0">
            {(() => {
              const startOfWeek = new Date(year, month, day - currentDate.getDay());
              return Array.from({ length: 7 }, (_, i) => {
                const d = new Date(startOfWeek); d.setDate(d.getDate() + i);
                const ds = localDateStr(d);
                const dayTasks = getTasksForDate(ds);
                const isToday = ds === todayStr;
                const isWeekend = i === 0 || i === 6;
                const cellBg = isToday
                  ? 'border-gold/40 bg-gold/[0.06] ring-1 ring-gold/20'
                  : isWeekend
                    ? 'border-border/30 bg-foreground/[0.02] hover:bg-foreground/[0.04]'
                    : 'border-border/30 hover:bg-foreground/[0.02]';
                return (
                  <div
                    key={i}
                    className={`min-h-[300px] rounded-lg p-2 border cursor-pointer ${cellBg}`}
                    onClick={() => setSidePanelDate(ds)}
                    data-testid={`calendar-week-cell-${ds}`}
                  >
                    <div className="text-center mb-2">
                      <p className="text-[10px] text-muted-foreground/50">{DAY_NAMES[i]}</p>
                      <p className={`text-[15px] font-semibold ${isToday ? 'text-gold' : ''}`}>{d.getDate()}</p>
                    </div>
                    <div className="space-y-1">{dayTasks.map(occ => <TaskCard key={occ.key} occurrence={occ} />)}</div>
                    {dayTasks.length === 0 && <p className="text-[9px] text-muted-foreground/20 text-center mt-8">No tasks</p>}
                  </div>
                );
              });
            })()}
          </div>
        </CardContent></Card>
      )}

      {/* ─── DAILY VIEW ─── */}
      {viewMode === 'daily' && (() => {
        const ds = localDateStr(currentDate);
        const dayTasks = getTasksForDate(ds);
        const isToday = ds === todayStr;
        return (
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <span className={`text-[14px] font-medium ${isToday ? 'text-gold' : ''}`}>{formatTaskDate(ds)}</span>
            </div>
            {dayTasks.length === 0 ? (
              <Card className="card-hover-gold"><CardContent className="py-16 text-center text-muted-foreground/40 text-[13px]">No tasks for this day</CardContent></Card>
            ) : (
              <div className="space-y-1.5">
                {dayTasks.slice().sort((a, b) => {
                  // Sort by underlying task fields, regardless of occurrence kind
                  const ta = a.task, tb = b.task;
                  if (ta.status === 'completed' && tb.status !== 'completed') return 1;
                  if (ta.due_date < todayStr && tb.due_date >= todayStr) return -1;
                  const po = { high: 0, medium: 1, low: 2 };
                  return (po[ta.priority] ?? 1) - (po[tb.priority] ?? 1);
                }).map(occ => {
                  const task = occ.task;
                  const isFollowUp = occ.kind === 'followup';
                  return (
                    <button key={occ.key} onClick={() => setSelectedTaskId(task.id)} className={`flex items-center gap-3 w-full p-3 rounded-lg border-l-2 task-row-hover text-left ${getTaskColor(task, todayStr)} ${isFollowUp ? 'opacity-65 border-dashed' : ''}`}>
                      {isFollowUp ? (
                        <Flag className="h-3.5 w-3.5 text-muted-foreground/60 shrink-0" />
                      ) : (
                        task.status !== 'completed' && task.status !== 'cancelled' && (
                          <span className={`h-2 w-2 rounded-full shrink-0 ${PRIORITY_DOT[task.priority]}`} />
                        )
                      )}
                      <div className="flex-1 min-w-0">
                        <p className={`text-[13px] font-medium ${task.status === 'completed' ? 'line-through text-muted-foreground/50' : ''}`}>{task.title}{isFollowUp && occ.followUp?.label ? <span className="text-muted-foreground/50 ml-2 text-[11px] font-normal">— {occ.followUp.label}</span> : null}</p>
                        <div className="flex items-center gap-2 mt-0.5 text-[11px] text-muted-foreground/50">
                          {isFollowUp && <span className="text-amber-400/60 text-[10px] font-semibold uppercase">Checkpoint</span>}
                          <TaskStatusIndicator status={task.status} size="xs" />
                          {task.status !== 'completed' && task.status !== 'cancelled' && <span className="capitalize">{task.priority}</span>}
                          {(task.assignee_details || []).length > 0 && <span>{task.assignee_details.map(a => a.name).join(', ')}</span>}
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        );
      })()}

      {/* ─── SIDE PANEL: tasks for clicked date ─── */}
      <Sheet open={!!sidePanelDate} onOpenChange={(o) => { if (!o) setSidePanelDate(null); }}>
        <SheetContent side="right" className="w-full sm:max-w-md bg-card border-border/40">
          <SheetHeader>
            <SheetTitle className="font-display text-[16px]">{sidePanelDate ? formatTaskDate(sidePanelDate) : ''}</SheetTitle>
            <p className="text-[11px] text-muted-foreground/50">{sidePanelTasks.length} task{sidePanelTasks.length === 1 ? '' : 's'}</p>
          </SheetHeader>
          <div className="mt-4 space-y-1.5 overflow-y-auto" style={{ maxHeight: 'calc(100vh - 120px)' }} data-testid="calendar-sidepanel-list">
            {sidePanelTasks.length === 0 ? (
              <p className="text-[12px] text-muted-foreground/40 text-center py-12">No tasks due on this date.</p>
            ) : (
              sidePanelTasks
                .slice()
                .sort((a, b) => {
                  // Due-date occurrences before follow-up checkpoints when both exist for same task
                  if (a.kind === 'due' && b.kind !== 'due') return -1;
                  if (a.kind !== 'due' && b.kind === 'due') return 1;
                  const ta = a.task, tb = b.task;
                  if (ta.status === 'completed' && tb.status !== 'completed') return 1;
                  if (ta.status !== 'completed' && tb.status === 'completed') return -1;
                  const po = { high: 0, medium: 1, low: 2 };
                  return (po[ta.priority] ?? 1) - (po[tb.priority] ?? 1);
                })
                .map(occ => {
                  const task = occ.task;
                  const isFollowUp = occ.kind === 'followup';
                  return (
                    <button
                      key={occ.key}
                      onClick={() => { setSelectedTaskId(task.id); setSidePanelDate(null); }}
                      className={`flex items-center gap-3 w-full p-3 rounded-lg border-l-2 hover:bg-foreground/[0.04] text-left ${getTaskColor(task, todayStr)} ${isFollowUp ? 'opacity-65 border-dashed' : ''}`}
                      data-testid={`calendar-sidepanel-task-${task.id}${isFollowUp ? `-fu-${occ.followUpIndex}` : ''}`}
                    >
                      {isFollowUp ? (
                        <Flag className="h-3.5 w-3.5 text-muted-foreground/60 shrink-0" />
                      ) : (
                        task.status !== 'completed' && task.status !== 'cancelled' && (
                          <span className={`h-2 w-2 rounded-full shrink-0 ${PRIORITY_DOT[task.priority]}`} />
                        )
                      )}
                      <div className="flex-1 min-w-0">
                        <p className={`text-[13px] font-medium ${task.status === 'completed' ? 'line-through text-muted-foreground/50' : ''}`}>
                          {task.title}
                          {isFollowUp && occ.followUp?.label ? <span className="text-muted-foreground/50 ml-2 text-[11px] font-normal">— {occ.followUp.label}</span> : null}
                        </p>
                        <div className="flex items-center gap-2 mt-0.5 text-[11px] text-muted-foreground/50">
                          {isFollowUp && <span className="text-amber-400/60 text-[10px] font-semibold uppercase">Checkpoint</span>}
                          <TaskStatusIndicator status={task.status} size="xs" />
                          {task.status !== 'completed' && task.status !== 'cancelled' && <span className="capitalize">{task.priority}</span>}
                          {(task.assignee_details || []).length > 0 && <span>{task.assignee_details.map(a => a.name).join(', ')}</span>}
                        </div>
                      </div>
                    </button>
                  );
                })
            )}
          </div>
        </SheetContent>
      </Sheet>

      <TaskDetailSheet taskId={selectedTaskId} open={!!selectedTaskId} onOpenChange={o => { if (!o) setSelectedTaskId(null); }} onUpdate={fetchTasks} />
    </div>
  );
}
